import { useCallback } from 'react';
import type { AuthUser } from '@/types/auth';

/**
 * Returns a redirect path based on the user's activation state,
 * or null if the user should proceed normally.
 *
 * Replicates AngularJS login.service.ts activation logic (lines 312-404).
 *
 * Check order:
 * 1. !active → force logout
 * 2. ITK + no lastLogin → update password
 * 3. !lastLogin → patch lastLogin, continue
 * 4. role === 0 + KYC incomplete → /auth/set-up
 * 5. !isConfirmed && role === 0 → /auth/confirm
 * 6. !emailVerified → /auth/welcome
 * 7. Hotsale without subscription → profile (checkout not yet migrated)
 * 8. Demo expired without subscription → profile with payment warning
 * 9. Otherwise → null (proceed to dashboard)
 */

interface ActivationResult {
  redirect: string | null;
  shouldLogout: boolean;
  shouldPatchLastLogin: boolean;
  message?: string;
}

const KYC_REQUIRED_FIELDS = [
  'position',
  'servicesTypes',
  'vehiclesTypes',
  'hasMultipleBranches',
  'clientTypes',
  'communicationMethods',
  'hasDefinedProcesses',
  'hasTesterReady',
  'implementationTimeline',
] as const;

function isKycIncomplete(kyc: AuthUser['kyc']): boolean {
  if (!kyc) return false; // no kyc object means skip check
  return (
    !kyc.position ||
    !kyc.servicesTypes?.length ||
    !kyc.vehiclesTypes?.length ||
    !kyc.hasMultipleBranches ||
    !kyc.clientTypes?.length ||
    !kyc.communicationMethods?.length ||
    !kyc.hasDefinedProcesses ||
    !kyc.hasTesterReady ||
    !kyc.implementationTimeline
  );
}

export function useActivationGuard() {
  const checkActivation = useCallback((user: AuthUser): ActivationResult => {
    // 1. Not active → force logout
    if (!user.active) {
      return {
        redirect: null,
        shouldLogout: true,
        shouldPatchLastLogin: false,
        message: 'Tu cuenta está inactiva. Contacta a soporte.',
      };
    }

    // 2. ITK user with no previous login → force password update
    if (user.businessConfig?.itk?.is && !user.lastLogin) {
      return {
        redirect: '/auth/update-password',
        shouldLogout: false,
        shouldPatchLastLogin: false,
      };
    }

    // 3. First login → need to patch lastLogin
    const shouldPatchLastLogin = !user.lastLogin;

    // 4. Admin (role 0) with incomplete KYC → KYC wizard
    if (user.role === 0 && user.kyc && isKycIncomplete(user.kyc)) {
      return {
        redirect: '/auth/set-up',
        shouldLogout: false,
        shouldPatchLastLogin,
      };
    }

    // 5. Not confirmed and admin → confirmation page
    if (!user.isConfirmed && user.role === 0) {
      return {
        redirect: '/auth/confirm',
        shouldLogout: false,
        shouldPatchLastLogin,
      };
    }

    // 6. Email not verified → welcome/onboarding
    if (user.emailVerified === false || user.emailVerified === null) {
      return {
        redirect: '/auth/welcome',
        shouldLogout: false,
        shouldPatchLastLogin,
      };
    }

    // 7. Hotsale without subscription → redirect to profile
    if (user.vendor === 'hotsale' && !user.subscription) {
      return {
        redirect: '/shop/profile',
        shouldLogout: false,
        shouldPatchLastLogin,
      };
    }

    // 8. Demo expired without subscription
    if ((user.lastDaysForDemo ?? 0) <= 0 && !user.subscription) {
      return {
        redirect: '/shop/profile',
        shouldLogout: false,
        shouldPatchLastLogin,
        message: 'Tu periodo de prueba ha terminado. Selecciona un plan.',
      };
    }

    // 9. All clear → proceed normally
    return {
      redirect: null,
      shouldLogout: false,
      shouldPatchLastLogin,
    };
  }, []);

  return checkActivation;
}
